import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SiteWidgets from '../../components/feature/SiteWidgets';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type Filter = 'All' | 'Solar' | 'Battery' | 'EV Charger' | 'Bundle';

const projects = [
  {
    id: 1,
    type: 'Solar',
    title: '6kW Solar Installation',
    location: 'Chelmsford, Essex',
    desc: '15 premium monocrystalline panels installed across a south-facing roof. System generating 5,800 kWh per year.',
    specs: ['6kW System', '15 Panels', '25-yr Warranty'],
    saving: '£1,100/yr',
    image: 'https://public.readdy.ai/ai/img_res/d9e2d505ad5b7e961c549dd4e2757a4e.jpg',
  },
  {
    id: 2,
    type: 'Bundle',
    title: 'Solar + Tesla Powerwall',
    location: 'Brentwood, Essex',
    desc: 'Full solar and battery bundle — 4kW panels paired with a Tesla Powerwall 3. Near total energy independence.',
    specs: ['4kW Solar', 'Tesla Powerwall 3', 'Finance Deal'],
    saving: '£1,700/yr',
    image: 'https://readdy.ai/api/search-image?query=UK%20detached%20home%20with%20solar%20panels%20on%20roof%20and%20Tesla%20Powerwall%20battery%20mounted%20on%20garage%20wall%2C%20daytime%20professional%20real%20estate%20photography%2C%20modern%20house%2C%20clean%20driveway&width=600&height=420&seq=proj-bundle-1&orientation=landscape',
  },
  {
    id: 3,
    type: 'EV Charger',
    title: 'Zappi Solar EV Charger',
    location: 'Ipswich, Suffolk',
    desc: 'Zappi V2 7.4kW smart EV charger installed to divert excess solar energy directly into the customer\'s Tesla Model Y.',
    specs: ['7.4kW Zappi V2', 'Solar Compatible', 'Smart App'],
    saving: '£900/yr',
    image: 'https://readdy.ai/api/search-image?query=Zappi%20EV%20charger%20mounted%20on%20side%20wall%20of%20UK%20family%20home%2C%20Tesla%20Model%20Y%20car%20in%20driveway%20charging%2C%20clean%20modern%20installation%2C%20professional%20photography%2C%20daytime&width=600&height=420&seq=proj-ev-1&orientation=landscape',
  },
  {
    id: 4,
    type: 'Solar',
    title: '4kW Solar Installation',
    location: 'Cambridge',
    desc: '10-panel system on a semi-detached Victorian home. Optimisers fitted to handle partial shading from a chimney stack.',
    specs: ['4kW System', '10 Panels', 'Optimisers'],
    saving: '£780/yr',
    image: 'https://readdy.ai/api/search-image?query=solar%20panels%20on%20Victorian%20semi%20detached%20UK%20house%20rooftop%2C%2010%20monocrystalline%20panels%2C%20professional%20installation%2C%20sunny%20day%20Cambridge%2C%20close%20aerial%20view&width=600&height=420&seq=proj-solar-2&orientation=landscape',
  },
  {
    id: 5,
    type: 'Battery',
    title: 'GivEnergy 9.5kWh Battery',
    location: 'Romford, London',
    desc: 'Retrofit battery storage added to an existing 3kW solar system. Now using 90% of solar generation and charging on Octopus Go overnight.',
    specs: ['9.5kWh GivEnergy', 'Retrofit Install', 'Smart Tariff'],
    saving: '£620/yr',
    image: 'https://readdy.ai/api/search-image?query=GivEnergy%20home%20battery%20storage%20unit%20mounted%20on%20clean%20white%20garage%20interior%20wall%2C%20professional%20installation%2C%20modern%20UK%20home%2C%20energy%20storage%20technology%20photography&width=600&height=420&seq=proj-battery-1&orientation=landscape',
  },
  {
    id: 6,
    type: 'EV Charger',
    title: 'Commercial Car Park — 12 Bays',
    location: 'Stratford, London',
    desc: '12-bay commercial EV charging installation for a retail car park. OZEV-approved, load-balanced smart system with 24/7 monitoring.',
    specs: ['12 Charge Points', 'Load Balanced', 'OZEV Approved'],
    saving: 'POA',
    image: 'https://readdy.ai/api/search-image?query=commercial%20car%20park%20with%20multiple%20EV%20charging%20stations%20installed%2C%20modern%20retail%20parking%20lot%2C%20electric%20vehicle%20charging%20bays%2C%20professional%20installation%20UK%2C%20daytime%20photography&width=600&height=420&seq=proj-commercial-ev&orientation=landscape',
  },
  {
    id: 7,
    type: 'Bundle',
    title: '10kW Solar + GivEnergy 15kWh',
    location: 'Colchester, Essex',
    desc: 'Large detached home — 24 panels across two roof pitches with a GivEnergy All-in-One. 95% self-sufficiency achieved.',
    specs: ['10kW System', '24 Panels', 'GivEnergy All-in-One'],
    saving: '£2,200/yr',
    image: 'https://readdy.ai/api/search-image?query=large%20detached%20UK%20country%20home%20with%2024%20solar%20panels%20across%20two%20roof%20pitches%2C%20professional%20aerial%20photography%2C%20sunny%20day%2C%20lush%20garden%2C%20modern%20property%2C%20green%20energy&width=600&height=420&seq=proj-bundle-2&orientation=landscape',
  },
  {
    id: 8,
    type: 'Solar',
    title: '3kW Apartment Block Solar',
    location: 'Hackney, London',
    desc: 'Flat-roof solar installation on a London apartment block. Shared generation feeding communal electricity meter.',
    specs: ['3kW Flat Roof', 'Communal Meter', 'MCS Certified'],
    saving: '£540/yr',
    image: 'https://readdy.ai/api/search-image?query=flat%20roof%20solar%20panel%20installation%20on%20London%20apartment%20block%20building%2C%20commercial%20solar%20panels%20on%20flat%20roof%2C%20urban%20setting%2C%20professional%20photography%2C%20aerial%20view&width=600&height=420&seq=proj-solar-3&orientation=landscape',
  },
  {
    id: 9,
    type: 'EV Charger',
    title: 'Ohme Home Pro Installation',
    location: 'Woodford, Essex',
    desc: 'Ohme Home Pro installed in a new build garage. Integration with Octopus Energy smart tariff for automatic overnight charging.',
    specs: ['7.4kW Ohme Pro', 'Octopus Ready', 'New Build'],
    saving: '£800/yr',
    image: 'https://readdy.ai/api/search-image?query=Ohme%20Home%20Pro%20EV%20charger%20installed%20in%20modern%20new%20build%20UK%20garage%2C%20clean%20white%20walls%2C%20electric%20car%20visible%2C%20professional%20installation%20photography%2C%20neat%20cable%20management&width=600&height=420&seq=proj-ev-2&orientation=landscape',
  },
];

const filterColors: Record<Filter, string> = {
  All: '#145A32',
  Solar: '#f59e0b',
  Battery: '#3b82f6',
  'EV Charger': '#145A32',
  Bundle: '#4CAF50',
};

const typeBadgeColors: Record<string, string> = {
  Solar: 'bg-amber-100 text-amber-700',
  Battery: 'bg-blue-100 text-blue-700',
  'EV Charger': 'bg-[#145A32]/10 text-[#145A32]',
  Bundle: 'bg-green-100 text-green-700',
};

export default function ProjectsPage() {
  const [visible, setVisible] = useState(false);
  const [filter, setFilter] = useState<Filter>('All');
  const headingAnim = useScrollAnimation();
  const gridAnim = useScrollAnimation();
  const statsAnim = useScrollAnimation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = filter === 'All' ? projects : projects.filter((p) => p.type === filter);

  const stats = [
    { icon: 'ri-home-4-line', value: '500+', label: 'Installations completed' },
    { icon: 'ri-sun-line', value: '2.1MW', label: 'Solar capacity installed' },
    { icon: 'ri-battery-2-charge-line', value: '180+', label: 'Batteries installed' },
    { icon: 'ri-charging-pile-line', value: '350+', label: 'EV chargers fitted' },
  ];

  return (
    <main className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="bg-[#145A32] pt-24 md:pt-40 pb-12 md:pb-16 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></div>
            Back to home
          </Link>
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">OUR WORK</div>
            <h1 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight mb-4 max-w-2xl">
              Real installations.<br />Real savings.
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed">
              Browse completed solar, battery and EV charging projects across London, Essex, Suffolk and Cambridge. Every install is fixed-price, MCS &amp; OZEV certified.
            </p>
          </div>

          {/* Stats row */}
          <div
            ref={statsAnim.ref}
            className={`grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 transition-all duration-700 ${statsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {stats.map((s, i) => (
              <div
                key={s.label}
                style={{ transitionDelay: `${i * 80}ms` }}
                className="bg-white/10 rounded-xl px-4 py-4 text-center"
              >
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className={`${s.icon} text-[#7AC943] text-xl`} />
                </div>
                <div className="text-white font-black text-2xl leading-none mb-1">{s.value}</div>
                <div className="text-white/60 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-12 md:py-16 bg-[#f7f8fa]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">

          {/* Filter bar */}
          <div
            ref={headingAnim.ref}
            className={`flex flex-wrap gap-2 mb-8 justify-center transition-all duration-700 ${headingAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            {(['All', 'Solar', 'Battery', 'EV Charger', 'Bundle'] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${filter === f ? 'bg-[#145A32] text-white' : 'bg-white text-[#145A32] border-2 border-[#145A32]/20 hover:border-[#145A32]'}`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Projects grid */}
          <div ref={gridAnim.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => (
              <div
                key={p.id}
                style={{ transitionDelay: `${i * 70}ms` }}
                className={`bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 group ${gridAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${typeBadgeColors[p.type] || 'bg-gray-100 text-gray-600'}`}>
                      {p.type}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 bg-[#145A32] text-white text-[11px] font-black px-2.5 py-1 rounded-full">
                    Saves {p.saving}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading font-bold text-[#1F2933] text-base leading-tight">{p.title}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-pin-2-line text-[#4CAF50] text-xs" />
                    </div>
                    <span className="text-gray-400 text-xs">{p.location}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{p.desc}</p>

                  {/* Spec pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {p.specs.map((spec) => (
                      <span key={spec} className="text-[10px] bg-[#f7f8fa] text-[#145A32] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-10">
            <p className="text-gray-500 text-sm mb-4">Want a project like this for your home or business?</p>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openBooking'))}
              className="shimmer-btn text-white font-bold px-8 py-3.5 rounded-full text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
            >
              Get a free quote →
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <SiteWidgets />
    </main>
  );
}
